// =====================================================================
// src/modulos/seguranca.js — Segurança: quem está no prédio, ocorrências
// e o histórico de ocupação de um apartamento (0057)
//
// O QUE O HUGO DECIDIU (23/09/2026), e que esta tela obedece:
//   · 3A — a segurança vê o mínimo do hóspede: nome, apartamento, hora de
//     entrada e se é menor. Nada de documento, telefone ou FNRH.
//   · "Imprimir lista de emergência" — para falta de luz ou de internet.
//   · a) A — o proprietário não vê ocorrência nenhuma.
//   · b) A + supervisão — registram: Segurança, Administração, Supervisão.
//   · categorias editáveis por hotel (Configurações).
//   · a ocorrência não se apaga: encerra-se, com observação.
//
// DE ONDE VEIO O DESENHO
//
// O Monteiro não tem tela de segurança — o prédio dele é de flats sem
// guarita. Então esta tela é montada com o que este app já copiou de lá:
//   · as abas: `barraDeAbas` (`_reservasTabBar`, 2508);
//   · os números do topo: o `statCard` do dashboard (1937);
//   · a lista por andar: `table.table-cards` com `data-label` (`renderGaragem`,
//     8853), que no celular vira cartão sozinha;
//   · o cartão da ocorrência: o de `_renderIntercorrenciaItem` (23291), o
//     mesmo que a Manutenção já usa (`.chamado`);
//   · o modal de registrar: `modalNovaIntercorrencia` (23043), com a foto
//     por câmera ao vivo e queda para a câmera do sistema — cópia do que a
//     governança já faz na intercorrência;
//   · o PDF: `src/lib/exportar.js` (jsPDF + autoTable, `exportarHospedesPDF`).
//
// ONDE DIVERGE, E POR QUÊ
//   · A lista de emergência é PDF para imprimir, não `window.print()` da
//     página: a página tem menu, botões e cores; o bombeiro precisa de uma
//     tabela em preto e branco, andar por andar, com a hora em que foi
//     gerada. E o PDF fica salvo no computador da portaria para quando a
//     internet cair — que é justamente quando ele vai ser usado.
//   · "Quando aconteceu" é um campo, com o agora preenchido: a segurança
//     costuma registrar depois de resolver, e a hora que importa é a do fato.
//
// QUEM VÊ O QUÊ NÃO ESTÁ AQUI. Está no RLS e nas funções da 0057.
// =====================================================================
import { el, icone, abrirModal, fecharModal, aviso, barraDeAbas, enquantoAtualiza } from '../ui.js';
import { hojeDe } from '../lib/contexto.js';
import { dataCurta } from '../lib/manutencao.js';
import { paraPdf, temPdf, nomeDoArquivo } from '../lib/exportar.js';
import {
  SITUACOES, agruparPorAndar, resumoDoPredio, filtrarHospedados, dataHora,
  COLUNAS_EMERGENCIA, linhasDeEmergencia, COLUNAS_HISTORICO, linhasDeHistorico,
  paraCampoDataHora, validarOcorrencia, argumentosDaOcorrencia, periodoPadrao, validarPeriodo, podeRegistrar,
} from '../lib/seguranca.js';

export function montar(cont, { auth, contexto, agora = () => Date.now() }) {
  const sistema = !!(contexto.usuario && contexto.usuario.sistema);
  // O Administrador do Sistema não tem cliente (CLAUDE.md §3.2): escolhe o
  // hotel num seletor e nomeia-o em `p_cliente` (0057).
  let clienteSel = sistema ? null : contexto.cliente.id;
  let clientes = [];
  const pc = () => (sistema ? { p_cliente: clienteSel } : {});
  const hoje = () => hojeDe(contexto, agora());
  const registra = sistema || podeRegistrar(contexto);
  let vivo = true;
  let aba = 'agora';
  let hospedados = [], ocorrencias = [], categorias = [], unidades = [], historico = null;
  const filtro = { texto: '', situacao: 'aberta', ...periodoPadrao(hoje()) };
  const hist = { unidade: '', ...periodoPadrao(hoje()) };

  const hotel = () => (sistema ? ((clientes.find(c => c.id === clienteSel) || {}).nome) : (contexto.cliente && contexto.cliente.nome)) || 'Hotel';
  const slug = () => (sistema ? ((clientes.find(c => c.id === clienteSel) || {}).slug) : (contexto.cliente && contexto.cliente.slug)) || 'hotel';

  async function carregar() {
    if (sistema) { clientes = await auth.ler('clientes', 'select=id,slug,nome&order=nome'); if (!clienteSel && clientes.length) clienteSel = clientes[0].id; }
    if (!clienteSel) { hospedados = []; ocorrencias = []; categorias = []; unidades = []; return; }
    const [h, o, c, u] = await Promise.all([
      auth.rpc('hospedados_agora', { ...pc() }),
      auth.rpc('ocorrencias_do_cliente', { p_situacao: filtro.situacao || null, p_de: filtro.de, p_ate: filtro.ate, ...pc() }),
      auth.ler('categorias_de_ocorrencia', `select=id,nome,ativa,ordem&cliente_id=eq.${clienteSel}&order=ordem`),
      auth.ler('unidades', `select=id,numero,andar&ativo=eq.true&cliente_id=eq.${clienteSel}&order=numero`),
    ]);
    hospedados = h || []; ocorrencias = o || []; categorias = c || []; unidades = u || [];
  }
  function refazer() {
    return enquantoAtualiza(async () => {
      try { await carregar(); } catch (e) { aviso(e.message, 'erro'); }
      desenhar();
    });
  }

  function desenhar() {
    if (!vivo) return;
    cont.replaceChildren();
    cont.dataset.tela = 'seguranca';
    if (sistema) cont.appendChild(el('div', { class: 'usu-filtros' },
      el('select', { class: 'input', id: 'sg-cliente', style: { width: 'auto' }, onchange: ev => { clienteSel = ev.target.value; historico = null; refazer(); } },
        clientes.map(c => el('option', { value: c.id, selected: c.id === clienteSel }, c.nome)))));
    cont.appendChild(barraDeAbas('sg-abas', [
      { chave: 'agora', rotulo: 'Hospedados agora' },
      { chave: 'ocorrencias', rotulo: 'Ocorrências' },
      { chave: 'historico', rotulo: 'Histórico de ocupação' },
    ], aba, chave => { aba = chave; desenhar(); }));
    if (aba === 'agora') desenharAgora();
    else if (aba === 'ocorrencias') desenharOcorrencias();
    else desenharHistorico();
  }

  // ── 1. QUEM ESTÁ NO PRÉDIO ─────────────────────────────────────────
  // `statCard` (1937) — os três números que a segurança diz no rádio.
  const statCard = (label, val, cor, id) => el('div', { class: 'stat-card', id },
    el('div', { style: { fontSize: '20px', fontWeight: '700', color: cor } }, String(val)),
    el('div', { class: 'stat-label' }, label));

  function desenharAgora() {
    const r = resumoDoPredio(hospedados);
    cont.appendChild(el('div', { class: 'contadores', id: 'sg-resumo' },
      statCard('pessoas no prédio', r.pessoas, '#1B3A7A', 'sg-pessoas'),
      statCard('apartamentos ocupados', r.apartamentos, '#1B3A7A', 'sg-aptos'),
      statCard('menores', r.menores, r.menores ? '#d97706' : '#6b7280', 'sg-menores')));

    cont.appendChild(el('div', { class: 'gov-cabeca' },
      el('input', { type: 'search', class: 'input', id: 'sg-filtro', placeholder: 'Apartamento ou nome', value: filtro.texto, style: { maxWidth: '280px' },
        oninput: ev => { filtro.texto = ev.target.value; desenharLista(); } }),
      el('div', { style: { display: 'flex', gap: '8px', flexWrap: 'wrap' } },
        el('button', { type: 'button', class: 'btn btn-secondary btn-sm', id: 'sg-atualizar', onclick: refazer }, icone('ic-refresh'), 'Atualizar'),
        temPdf() ? el('button', { type: 'button', class: 'btn btn-primary btn-sm', id: 'sg-imprimir', onclick: imprimirEmergencia }, icone('ic-pdf'), 'Imprimir lista de emergência') : null)));
    cont.appendChild(el('div', { id: 'sg-lista' }));
    desenharLista();
  }

  function desenharLista() {
    const lugar = cont.querySelector('#sg-lista');
    if (!lugar) return;
    const linhas = filtrarHospedados(hospedados, filtro.texto);
    if (!linhas.length) {
      lugar.replaceChildren(el('div', { class: 'card' }, el('p', { class: 'vazio', id: 'sg-vazio' },
        hospedados.length ? 'Ninguém com esse apartamento ou nome.' : 'Ninguém fez check-in que ainda não tenha saído.')));
      return;
    }
    lugar.replaceChildren(...agruparPorAndar(linhas).map(a => el('div', { class: 'card', style: { marginBottom: '12px', overflowX: 'auto' }, dataset: { andar: a.andar == null ? '' : String(a.andar) } },
      el('h3', { class: 'secao-titulo', style: { marginTop: '0' } }, `${a.rotulo} · ${a.apartamentos.reduce((n, ap) => n + ap.pessoas.length, 0)} pessoa(s)`),
      el('table', { class: 'table-cards' },
        el('thead', {}, el('tr', {}, el('th', {}, 'Apto'), el('th', {}, 'Nome'), el('th', {}, 'Entrada'), el('th', {}, ''))),
        el('tbody', {}, a.apartamentos.flatMap(ap => ap.pessoas.map((p, i) =>
          el('tr', { dataset: { unidade: ap.unidade, hospedado: p.nome } },
            el('td', { 'data-label': 'Apto', style: { fontWeight: '700' } }, i === 0 ? ap.unidade : ''),
            el('td', { 'data-label': 'Nome' }, p.nome),
            el('td', { 'data-label': 'Entrada', style: { fontVariantNumeric: 'tabular-nums' } }, dataCurta(p.chegada_em, agora())),
            el('td', { 'data-label': '' }, p.menor ? el('span', { class: 'badge badge-orange', dataset: { menor: '1' } }, 'Menor') : null)))))))));
  }

  function imprimirEmergencia() {
    try {
      const linhas = linhasDeEmergencia(hospedados);
      const r = resumoDoPredio(hospedados);
      paraPdf({
        titulo: 'Lista de emergência — quem está no prédio', hotel: hotel(),
        subtitulo: `Gerada em ${dataHora(new Date(agora()).toISOString())} · ${r.pessoas} pessoa(s) em ${r.apartamentos} apartamento(s) · ${r.menores} menor(es)`,
        colunas: COLUNAS_EMERGENCIA, linhas,
        arquivo: nomeDoArquivo('emergencia', [slug(), hoje()], 'pdf'),
      });
    } catch (e) { aviso(e.message, 'erro'); }
  }

  // ── 2. AS OCORRÊNCIAS ──────────────────────────────────────────────
  function desenharOcorrencias() {
    cont.appendChild(el('div', { class: 'gov-cabeca' },
      el('div', { class: 'usu-filtros', style: { marginBottom: '0' } },
        el('select', { class: 'input', id: 'sg-situacao', style: { minWidth: '140px' }, onchange: ev => { filtro.situacao = ev.target.value; refazer(); } },
          SITUACOES.map(s => el('option', { value: s.chave, selected: s.chave === filtro.situacao }, s.rotulo))),
        el('label', { class: 'label', style: { margin: '0' } }, 'De ',
          el('input', { type: 'date', class: 'input', id: 'sg-de', value: filtro.de, style: { minWidth: '0' }, onchange: ev => { filtro.de = ev.target.value; refazer(); } })),
        el('label', { class: 'label', style: { margin: '0' } }, 'até ',
          el('input', { type: 'date', class: 'input', id: 'sg-ate', value: filtro.ate, style: { minWidth: '0' }, onchange: ev => { filtro.ate = ev.target.value; refazer(); } }))),
      registra ? el('button', { type: 'button', class: 'btn btn-primary btn-sm', id: 'sg-nova', onclick: modalNova }, icone('ic-plus'), 'Nova ocorrência') : null));

    const lista = el('div', { id: 'sg-ocorrencias', style: { display: 'grid', gap: '10px' } });
    if (!ocorrencias.length) lista.appendChild(el('div', { class: 'card' }, el('p', { class: 'vazio' }, 'Nenhuma ocorrência neste período.')));
    for (const o of ocorrencias) lista.appendChild(cartao(o));
    cont.appendChild(lista);
  }

  // O cartão de `_renderIntercorrenciaItem` (23291), via `.chamado`.
  function cartao(o) {
    const encerrada = o.situacao === 'encerrada';
    const cor = encerrada ? '#059669' : '#dc2626';
    const caixa = el('div', { class: 'chamado' + (encerrada ? ' resolvido' : ''), dataset: { ocorrencia: o.id, situacao: o.situacao } });
    caixa.appendChild(el('div', { class: 'chamado-cabeca' },
      el('div', { class: 'quem', style: { color: cor } },
        icone(encerrada ? 'ic-check' : 'ic-shield'),
        o.unidade ? `Apto ${o.unidade}` : 'Prédio',
        o.andar != null ? el('span', { class: 'andar' }, ` · ${o.andar}º andar`) : null),
      el('div', { class: 'quando', title: `Registrada em ${dataHora(o.registrada_em)}` }, dataCurta(o.ocorrida_em, agora()))));
    caixa.appendChild(el('div', { class: 'chamado-titulo' }, el('span', { class: 'badge badge-blue' }, o.categoria)));
    if (o.foto_caminho) {
      const lugar = el('div', { class: 'chamado-foto' });
      const ver = el('button', { type: 'button', class: 'btn btn-secondary btn-sm', dataset: { foto: o.id },
        onclick: async () => {
          ver.disabled = true;
          try {
            const url = await auth.urlAssinada('ocorrencias', o.foto_caminho, 300);
            lugar.replaceChildren(el('img', { class: 'ficha-foto-previa', src: url, alt: 'Foto da ocorrência' }));
          } catch (e) { aviso(e.message, 'erro'); ver.disabled = false; }
        } }, icone('ic-camera'), 'Ver foto');
      lugar.appendChild(ver);
      caixa.appendChild(lugar);
    }
    caixa.appendChild(el('div', { class: 'chamado-descricao' }, o.descricao || ''));
    caixa.appendChild(el('div', { class: 'chamado-rodape' },
      el('span', { class: 'por' }, `Registrada por ${o.registrada_por_nome || '—'}`),
      encerrada
        ? el('span', { class: 'fechado' }, `Encerrada por ${o.encerrada_por_nome || '—'} em ${dataCurta(o.encerrada_em, agora())}`,
            o.encerrada_obs ? el('span', { class: 'obs' }, ` — ${o.encerrada_obs}`) : null)
        : (registra ? el('button', { type: 'button', class: 'btn btn-verde btn-sm', dataset: { encerrar: o.id }, onclick: () => modalEncerrar(o) }, icone('ic-check'), 'Encerrar') : null)));
    return caixa;
  }

  function erroModal(msg) { const e = document.getElementById('modal-erro'); if (e) { e.textContent = msg || ''; e.classList.toggle('hidden', !msg); } }

  function modalEncerrar(o) {
    const obs = el('textarea', { class: 'input', id: 'sg-obs', rows: '3', placeholder: 'O que foi feito? Ex.: hóspede advertido; silêncio restabelecido.' });
    const botao = el('button', { type: 'button', class: 'btn btn-verde', id: 'sg-confirmar-encerrar', style: { flex: '1' } }, icone('ic-check'), 'Encerrar');
    botao.onclick = async () => {
      if (!obs.value.trim()) { erroModal('Diga o que foi feito.'); obs.focus(); return; }
      botao.disabled = true;
      try { await auth.rpc('encerrar_ocorrencia', { p_id: o.id, p_obs: obs.value.trim(), ...pc() }); }
      catch (e) { erroModal(e.message); botao.disabled = false; return; }
      fecharModal(); aviso('Ocorrência encerrada.'); refazer();
    };
    abrirModal([
      el('h2', { class: 'modal-title' }, 'Encerrar ocorrência'),
      el('div', { style: { fontSize: '13px', color: '#6b7280', marginBottom: '10px' } }, `${o.categoria} · ${o.unidade ? 'Apto ' + o.unidade : 'Prédio'} · ${dataCurta(o.ocorrida_em, agora())}`),
      el('div', { id: 'modal-erro', class: 'alert alert-error hidden', role: 'alert' }),
      el('label', { class: 'label', for: 'sg-obs' }, 'O que foi feito'), obs,
      el('div', { style: { display: 'flex', gap: '10px', marginTop: '12px' } }, botao,
        el('button', { type: 'button', class: 'btn btn-secondary', style: { flex: '1' }, onclick: fecharModal }, 'Cancelar')),
    ]);
    obs.focus();
  }

  // ── registrar: `modalNovaIntercorrencia` (23043) ────────────────────
  // A foto: três estados, um de cada vez — os dois botões, o visor ao vivo,
  // a prévia. Cópia do que a governança faz (`abrirIntercorrencia`), que é
  // cópia de `_icAbrirCamera` / `_icCapturarCamera` do MdM (23111).
  function modalNova() {
    const ativas = categorias.filter(c => c.ativa);
    if (!ativas.length) { aviso('Nenhuma categoria ativa. Peça à administração para cadastrar em Configurações.', 'erro'); return; }
    let arquivo = null, fluxo = null;
    const raiz = el('div', { id: 'sg-nova-ocorrencia' });
    const cat = el('select', { class: 'input', id: 'sg-categoria' }, el('option', { value: '' }, '— escolha —'),
      ativas.map(c => el('option', { value: c.id }, c.nome)));
    const apto = el('select', { class: 'input', id: 'sg-unidade' }, el('option', { value: '' }, 'Prédio / área comum'),
      unidades.map(u => el('option', { value: u.id }, u.numero)));
    const quando = el('input', { type: 'datetime-local', class: 'input', id: 'sg-quando', value: paraCampoDataHora(agora()), max: paraCampoDataHora(agora() + 5 * 60 * 1000) });
    const desc = el('textarea', { class: 'input', id: 'sg-descricao', rows: '3', placeholder: 'O que aconteceu, quem estava envolvido e o que foi feito na hora.' });

    const previa = el('img', { class: 'ficha-foto-previa', id: 'sg-previa', alt: 'Foto da ocorrência' });
    const video = el('video', { id: 'sg-video', autoplay: true, playsinline: true, muted: true });
    const capturar = el('button', { type: 'button', class: 'capturar', id: 'sg-capturar' }, icone('ic-check'), 'Capturar');
    const visor = el('div', { class: 'ic-camera hidden', id: 'sg-visor' }, video,
      el('div', { class: 'ic-camera-botoes' },
        el('button', { type: 'button', class: 'desistir', onclick: () => { pararCamera(); mostrar('acoes'); } }, 'Cancelar'),
        capturar));
    const escolher = (comCamera) => {
      const velho = raiz.querySelector('#sg-foto');
      if (velho) velho.remove();
      const inp = el('input', { type: 'file', accept: 'image/*', id: 'sg-foto', style: { display: 'none' },
        onchange: ev => { const f = ev.target.files && ev.target.files[0] ? ev.target.files[0] : null; if (f) usarFoto(f); } });
      if (comCamera) inp.setAttribute('capture', 'environment');
      raiz.appendChild(inp);
      inp.click();
    };
    function usarFoto(f) { arquivo = f; previa.src = URL.createObjectURL(f); mostrar('previa'); }
    function pararCamera() {
      try { if (fluxo) fluxo.getTracks().forEach(t => t.stop()); } catch (_) { /* já parado */ }
      fluxo = null;
      try { video.srcObject = null; } catch (_) { /* idem */ }
    }
    async function abrirCamera() {
      const md = navigator.mediaDevices;
      if (!md || !md.getUserMedia) { escolher(true); return; }
      try {
        fluxo = await md.getUserMedia({ video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false });
      } catch (e) {
        aviso('Não consegui abrir a câmera aqui dentro; vou usar a câmera do aparelho.', 'erro');
        escolher(true);
        return;
      }
      video.srcObject = fluxo;
      try { await video.play(); } catch (_) { /* alguns navegadores já tocam sozinhos */ }
      mostrar('visor');
    }
    capturar.onclick = () => {
      const l = video.videoWidth || 1280, a = video.videoHeight || 720;
      const tela = document.createElement('canvas');
      tela.width = l; tela.height = a;
      tela.getContext('2d').drawImage(video, 0, 0, l, a);
      pararCamera();
      tela.toBlob(b => {
        if (!b) { aviso('Não consegui capturar a imagem. Tente pela galeria.', 'erro'); mostrar('acoes'); return; }
        usarFoto(b);
      }, 'image/jpeg', 0.85);
    };
    const acoes = el('div', { class: 'ic-foto-acoes', id: 'sg-foto-acoes' },
      el('button', { type: 'button', class: 'camera', onclick: abrirCamera }, icone('ic-camera'), 'Tirar foto agora'),
      el('button', { type: 'button', class: 'galeria', onclick: () => escolher(false) }, icone('ic-import'), 'Galeria / arquivo'));
    const trocar = el('button', { type: 'button', class: 'ic-trocar', id: 'sg-trocar-foto',
      onclick: () => { arquivo = null; previa.removeAttribute('src'); mostrar('acoes'); } }, icone('ic-refresh'), 'Trocar ou tirar outra foto');
    const blocoPrevia = el('div', { class: 'ic-previa hidden', id: 'sg-previa-bloco' }, previa, trocar);
    function mostrar(qual) {
      acoes.classList.toggle('hidden', qual !== 'acoes');
      visor.classList.toggle('hidden', qual !== 'visor');
      blocoPrevia.classList.toggle('hidden', qual !== 'previa');
    }

    const botao = el('button', { type: 'button', class: 'btn btn-contorno-vermelho', id: 'sg-salvar', style: { flex: '1' } }, icone('ic-shield'), 'Registrar ocorrência');
    botao.onclick = async () => {
      const dados = { categoria: cat.value, unidade: apto.value, descricao: desc.value, quando: quando.value };
      const erros = validarOcorrencia(dados, agora());
      if (erros.length) { erroModal(erros.join(' · ')); return; }
      botao.disabled = true;
      try {
        let caminho = null;
        if (arquivo) caminho = await auth.enviarFoto('ocorrencias', arquivo);
        await auth.rpc('registrar_ocorrencia', { ...argumentosDaOcorrencia(dados, caminho), ...pc() });
      } catch (e) { erroModal(e.message); botao.disabled = false; return; }
      fecharModal();
      aviso('Ocorrência registrada.');
      filtro.situacao = 'aberta';
      refazer();
    };

    raiz.appendChild(el('h2', { class: 'modal-title' }, 'Nova ocorrência'));
    raiz.appendChild(el('div', { id: 'modal-erro', class: 'alert alert-error hidden', role: 'alert' }));
    raiz.appendChild(el('div', { class: 'form-row' },
      el('div', {}, el('label', { class: 'label', for: 'sg-categoria' }, 'Categoria'), cat),
      el('div', {}, el('label', { class: 'label', for: 'sg-unidade' }, 'Onde'), apto)));
    raiz.appendChild(el('div', { style: { marginTop: '10px' } }, el('label', { class: 'label', for: 'sg-quando' }, 'Quando aconteceu'), quando));
    raiz.appendChild(el('div', { style: { marginTop: '10px' } }, el('label', { class: 'label', for: 'sg-descricao' }, 'O que aconteceu'), desc));
    raiz.appendChild(el('div', { style: { marginTop: '10px' } }, el('label', { class: 'label' }, 'Foto (opcional)'), acoes, visor, blocoPrevia));
    raiz.appendChild(el('div', { style: { display: 'flex', gap: '10px', marginTop: '14px' } }, botao,
      el('button', { type: 'button', class: 'btn btn-secondary', style: { flex: '1' }, onclick: fecharModal }, 'Cancelar')));
    abrirModal(raiz, { aoFechar: pararCamera });
    cat.focus();
  }

  // ── 3. O HISTÓRICO DE UM APARTAMENTO ───────────────────────────────
  function desenharHistorico() {
    const botao = el('button', { type: 'button', class: 'btn btn-primary btn-sm', id: 'sg-hist-buscar', onclick: buscarHistorico }, icone('ic-search'), 'Consultar');
    cont.appendChild(el('div', { class: 'usu-filtros' },
      el('select', { class: 'input', id: 'sg-hist-unidade', style: { minWidth: '140px' }, onchange: ev => { hist.unidade = ev.target.value; } },
        el('option', { value: '' }, '— apartamento —'),
        unidades.map(u => el('option', { value: u.id, selected: u.id === hist.unidade }, u.numero))),
      el('label', { class: 'label', style: { margin: '0' } }, 'De ',
        el('input', { type: 'date', class: 'input', id: 'sg-hist-de', value: hist.de, style: { minWidth: '0' }, onchange: ev => { hist.de = ev.target.value; } })),
      el('label', { class: 'label', style: { margin: '0' } }, 'até ',
        el('input', { type: 'date', class: 'input', id: 'sg-hist-ate', value: hist.ate, style: { minWidth: '0' }, onchange: ev => { hist.ate = ev.target.value; } })),
      botao));
    const lugar = el('div', { id: 'sg-historico' });
    cont.appendChild(lugar);
    if (historico === null) { lugar.appendChild(el('div', { class: 'card' }, el('p', { class: 'vazio' }, 'Escolha o apartamento e o período.'))); return; }
    const numero = (unidades.find(u => u.id === hist.unidade) || {}).numero || '';
    const linhas = linhasDeHistorico(historico);
    lugar.appendChild(el('div', { class: 'card', style: { overflowX: 'auto' } },
      el('div', { class: 'gov-cabeca' },
        el('h3', { class: 'secao-titulo', style: { margin: '0' } }, `Apto ${numero} · ${historico.length} pessoa(s)`),
        temPdf() && historico.length ? el('button', { type: 'button', class: 'btn btn-secondary btn-sm', id: 'sg-hist-pdf', onclick: () => {
          try {
            paraPdf({ titulo: `Histórico de ocupação — apto ${numero}`, hotel: hotel(),
              subtitulo: `${dataCurta(hist.de + 'T12:00:00', agora()).split(' ·')[0]} a ${dataCurta(hist.ate + 'T12:00:00', agora()).split(' ·')[0]} · ${historico.length} pessoa(s)`,
              colunas: COLUNAS_HISTORICO, linhas, arquivo: nomeDoArquivo('ocupacao', [slug(), numero, hist.de, hist.ate], 'pdf') });
          } catch (e) { aviso(e.message, 'erro'); }
        } }, icone('ic-pdf'), 'PDF') : null),
      historico.length
        ? el('table', { class: 'table-cards', id: 'sg-hist-tabela' },
            el('thead', {}, el('tr', {}, COLUNAS_HISTORICO.map(c => el('th', {}, c)))),
            el('tbody', {}, historico.map((h, i) => el('tr', { dataset: { pessoa: h.nome } },
              linhas[i].map((v, j) => el('td', { 'data-label': COLUNAS_HISTORICO[j] },
                j === 3 && v ? el('span', { class: 'badge badge-orange' }, 'Menor') : v))))))
        : el('p', { class: 'vazio', id: 'sg-hist-vazio' }, 'Ninguém esteve neste apartamento no período.')));
  }

  async function buscarHistorico() {
    if (!hist.unidade) { aviso('Escolha o apartamento.', 'erro'); return; }
    const erro = validarPeriodo(hist.de, hist.ate);
    if (erro) { aviso(erro, 'erro'); return; }
    await enquantoAtualiza(async () => {
      try { historico = await auth.rpc('historico_de_ocupacao', { p_unidade: hist.unidade, p_de: hist.de, p_ate: hist.ate, ...pc() }) || []; }
      catch (e) { aviso(e.message, 'erro'); return; }
      desenhar();
    });
  }

  refazer();
  return () => { vivo = false; };
}
